import React from 'react';
import { Facebook, Instagram } from 'lucide-react';
import useScrollAnimation from '../hooks/useScrollAnimation';

const AboutSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [imageRef, imageVisible] = useScrollAnimation({ threshold: 0.2 });
  const [razRef, razVisible] = useScrollAnimation({ threshold: 0.15 });
  const [assiRef, assiVisible] = useScrollAnimation({ threshold: 0.15 });
  const [statsRef, statsVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="about" className="bg-white py-24 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-20 w-64 h-64 bg-[#C5366A]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#3D4B8C]/5 rounded-full blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-[7.6923%] relative z-10">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 relative z-20 transition-all duration-700 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          dir="rtl"
        >
          <p className="text-[#C5366A] text-base font-medium mb-4 tracking-wide">
            מי אנחנו
          </p>
          <h2 className="text-[#2D2D2D] text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6">
            נעים להכיר, אסי ורז
          </h2>
          <p className="text-[#6B6B6B] text-lg max-w-3xl mx-auto">
            השותפים שבונים מערכי אפיליאייט עובדים מ-2018
          </p>
        </div>

        {/* Image with Parallax */}
        <div 
          ref={imageRef}
          className={`flex justify-center mb-16 relative z-10 transition-all duration-700 delay-100 ${
            imageVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <div className="relative">
            <img
              src="https://customer-assets.emergentagent.com/job_3a5ab85f-da0a-4b5c-ba39-99c6e6da22ed/artifacts/ouxtislg_image.png"
              alt="רז בליזובסקי ואסי אפשטיין"
              className="w-full max-w-[400px] md:max-w-[600px] object-cover rounded-xl shadow-xl"
            />
            {/* Decorative Accents */}
            <div className="absolute -bottom-4 -right-4 w-32 h-32 border-2 border-[#C5366A] opacity-30 rounded-lg pointer-events-none" />
            <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-[#3D4B8C] opacity-20 rounded-lg pointer-events-none" />
          </div>
        </div>

        {/* Founders Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" dir="rtl">
          {/* Raz */}
          <div 
            ref={razRef}
            className={`bg-[#F8EEF2] border border-[#C5366A]/10 p-8 rounded-xl hover:border-[#C5366A]/30 hover:shadow-lg transition-all duration-700 ${
              razVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#C5366A] flex items-center justify-center text-white text-xl font-bold rounded-lg">
                ר
              </div>
              <div>
                <h3 className="text-[#2D2D2D] text-2xl font-semibold">רז בליזובסקי</h3>
                <p className="text-[#C5366A] text-sm">יזם אינטרנט מרמת הגולן</p>
              </div>
            </div>
            
            <div className="space-y-4 text-[#6B6B6B] leading-relaxed">
              <p>
                התחיל את דרכו בדיגיטל ב-2015 בתור משווק אפיליאייט. בשנת 2017 רז הקים קמפיין אפיליאייט ראשון מסוגו בישראל והיה <span className="text-[#C5366A] font-medium">האפיליאייט הראשון בישראל שהביא יותר מ-1,600 לקוחות משלמים למוצר דיגיטלי בחודש וחצי בלבד</span>.
              </p>
              <p>
                כיום רז מלווה משווקי אפיליאייט בקבוצות וגם בייעוץ פרטני, והקים את חברת ב. אסטרטגיה שיווקית בע"מ כדי לשפר את איכות השיווק באמצעות אפיליאייט מרקטינג בישראל ולעזור לישראלים להפוך את התחום למקור ההכנסה העיקרי שלהם.
              </p>
              <p>
                בליזובסקי הוא איש משפחה, יועץ לשיווק דיגיטלי ואפיליאייט מרקטינג לעסקים וחברות ועזר לכמה חברות וארגונים <span className="text-[#C5366A] font-medium">לצמוח ב-400% בתוך 3 שנים</span> באמצעות שיווק חכם ושימוש במודל העסקי של אפיליאייט מרקטינג להגדלת מקורות ההכנסה של הארגון.
              </p>
            </div>
          </div>

          {/* Assi */}
          <div 
            ref={assiRef}
            className={`bg-[#F8EEF2] border border-[#C5366A]/10 p-8 rounded-xl hover:border-[#C5366A]/30 hover:shadow-lg transition-all duration-700 delay-150 ${
              assiVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#C5366A] flex items-center justify-center text-white text-xl font-bold rounded-lg">
                א
              </div>
              <div>
                <h3 className="text-[#2D2D2D] text-2xl font-semibold">אסי אפשטיין</h3>
                <p className="text-[#C5366A] text-sm">אחד מיזמי האינטרנט הראשונים בישראל</p>
              </div>
            </div>
            
            <div className="space-y-4 text-[#6B6B6B] leading-relaxed">
              <p>
                יזם אינטרנט סדרתי ומומחה באסטרטגיה שיווקית, קידום אתרים ושיווק באינטרנט. אפשטיין החל את דרכו המקצועית בחברת הייטק וב-2003 החל לפעול בתחום השיווק באינטרנט.
              </p>
              <p>
                הוא ידוע כמנטור לפרסום ושיווק ב-SEO וביוטיוב ופיתח שיטות ייחודיות לקידום עסקים באמצעות וידאו, כמו <span className="text-[#C5366A] font-medium">״קאשטיוב״</span> ו<span className="text-[#C5366A] font-medium">״מאנצ׳יז״</span>. אפשטיין מתמקד ביצירת הכנסות פאסיביות באמצעות נכסים דיגיטליים ומעביר תכניות והדרכות בנושא.
              </p>
              <p>
                אפשטיין הוא נווד דיגיטלי שמטייל בעולם, אב לשלושה, בעל ניסיון רב בהקמה, ניהול ותפעול של רשתות אפיליאט ונחשב <span className="text-[#C5366A] font-medium">לאחד המומחים המובילים בישראל ובעולם</span> בתחום קידום האתרים וקידום וידאו במנועי חיפוש.
              </p>
            </div>
          </div>
        </div>

        {/* Social Media Links */}
        <div className="mt-12 flex justify-center gap-6" dir="rtl">
          <a
            href="https://www.facebook.com/profile.php?id=61568896801638"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 px-6 py-3 bg-white border border-[#C5366A]/20 rounded-lg hover:border-[#C5366A]/50 hover:shadow-md transition-all duration-300"
            data-testid="social-facebook-link"
          >
            <Facebook className="w-5 h-5 text-[#6B6B6B] group-hover:text-[#C5366A] transition-colors" />
            <span className="text-[#6B6B6B] group-hover:text-[#2D2D2D] transition-colors">פייסבוק</span>
          </a>
          <a
            href="https://www.instagram.com/affiliate_academy_il/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 px-6 py-3 bg-white border border-[#C5366A]/20 rounded-lg hover:border-[#C5366A]/50 hover:shadow-md transition-all duration-300"
            data-testid="social-instagram-link"
          >
            <Instagram className="w-5 h-5 text-[#6B6B6B] group-hover:text-[#C5366A] transition-colors" />
            <span className="text-[#6B6B6B] group-hover:text-[#2D2D2D] transition-colors">אינסטגרם</span>
          </a>
        </div>

        {/* Stats */}
        <div 
          ref={statsRef}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6" 
          dir="rtl"
        >
          {[
            { value: '2018', label: 'שנת התחלת השותפות' },
            { value: '1,600+', label: 'לקוחות בחודש וחצי' },
            { value: '400%', label: 'צמיחה לחברות' },
            { value: '20+', label: 'שנות ניסיון משולב' },
          ].map((stat, index) => (
            <div 
              key={index}
              className={`text-center p-6 bg-[#F8EEF2] border border-[#C5366A]/10 rounded-xl transition-all duration-500 ${
                statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: statsVisible ? `${index * 100}ms` : '0ms' }}
            >
              <p className="text-[#C5366A] text-4xl md:text-5xl font-bold mb-2">{stat.value}</p>
              <p className="text-[#6B6B6B] text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
